import { MutableState } from 'src/replicant/State';
import {
  getStreakBattleRoyaleRemainingTime,
  getStreakBattleRoyaleStatus,
  isStreakBattleRoyaleActive,
  isStreakBattleRoyaleEnabled,
  isStreakBattleRoyaleCompleted,
  getStreakBattleRoyaleResetTimestamp,
  getTimeToNextStreakBattleRoyale,
  getStreakBattleRoyaleGoal,
} from './streakbattleroyale.getters';
import {
  STREAK_EVENT_DEFAULT_PROGRESSION,
  STREAK_EVENT_DEFAULT_THEME,
  StreakBattleRoyaleStatus,
} from './streakbattleroyale.ruleset';

/**
 * Sets the status of a streak event in the state.
 * @param state - The mutable state object.
 * @param status - The status to set for the streak event.
 */
export function setStreakBattleRoyaleStatus(
  state: MutableState,
  status: StreakBattleRoyaleStatus,
  now?: number,
): void {
  // skip if streak event feature is not enabled or not initialised.
  if (!state.streak) return;
  state.streak.status = status;

  if (status === 'failed' || status === 'rewarded') {
    state.streak.finishedTimestamp = now;
    state.streak.lastUserLifecycle = status;
  }
}

/**
 * Sets the `introSeen` flag to the specified value.
 *
 * @param state - The mutable state object.
 * @param value - The value to set the `introSeen` flag. Defaults to `true`.
 */
export function setStreakBattleRoyaleIntroSeen(
  state: MutableState,
  value = true,
): void {
  // skip if streak event feature is not enabled or not initialised.
  if (!isStreakBattleRoyaleEnabled(state) || !state.streak) return;
  state.streak.introSeen = value;
}

/**
 * Cache the last milestone seen on the front-end for the streak event.
 *
 * @param state - The mutable state object.
 * @param milestone - The milestone number to cache.
 */
export function setStreakBattleRoyaleLastMilestoneSeen(
  state: MutableState,
  milestone: number,
): void {
  // skip if streak event feature is not enabled or not initialised.
  if (!isStreakBattleRoyaleEnabled(state) || !state.streak) return;
  state.streak.lastMilestoneSeen = milestone;
}

/**
 * Update the streak event state by initialising it if not already,
 * flagging the status as 'expired',
 * or hard reset it if previously completed/failed before the expiration
 * and hence no expiration sequence handling is required.
 * @param state - The mutable state object.
 * @param now - The current timestamp.
 * @returns A result string indicating the outcome of the update.
 */
export function updateStreakBattleRoyaleState(
  state: MutableState,
  now: number,
): 'skipped' | 'initialized' | 'reset' | 'expired' {
  // Create streak event state if not already existed
  if (!state.streak) {
    state.streak = {
      completeCount: 0,
      introSeen: false,
      lastMilestoneSeen: 0,
      milestone: 0,
      status: 'available',
      resetTimestamp: getStreakBattleRoyaleResetTimestamp(now),
      theme: STREAK_EVENT_DEFAULT_THEME,
      progression: STREAK_EVENT_DEFAULT_PROGRESSION,
    };
    return 'initialized';
  }

  // If 'failed' status wasn't handled post-game, reset immediately.
  // ie. premature client refresh before actioning on the retry popup,
  if (state.streak.status === 'failed') {
    resetStreakBattleRoyale(state);
    return 'reset';
  }

  const completed = isStreakBattleRoyaleCompleted(state);

  // skip if the joined event is still ongoing and not yet completed.
  if (!completed && getStreakBattleRoyaleRemainingTime(state, now))
    return 'skipped';

  // skip if still within the same event period.
  if (getTimeToNextStreakBattleRoyale(state, now)) return 'skipped';

  const available = state.streak.status === 'available';

  // If currently in an 'available' state or previously completed,
  // hard reset the event state straight away.
  if (available || completed) {
    resetStreakBattleRoyale(state, now);
    return 'reset';
  }

  // Otherwise, just set it to 'expired' so that the expired flow can be shown before the reset.
  setStreakBattleRoyaleStatus(state, 'expired');
  return 'expired';
}

export function resetStreakBattleRoyale(
  state: MutableState,
  now?: number,
): void {
  // skip if streak event feature is not enabled or not initialised.
  if (!state.streak) return;

  /**
   * Updates the event reset timestamp, if now is supplied.
   * Also reset intro seen flag as a hard reset.
   */
  if (now) {
    // Reset to the latest default theme.
    state.streak.theme = STREAK_EVENT_DEFAULT_THEME;

    // Reset progression logic to latest default.
    state.streak.progression = STREAK_EVENT_DEFAULT_PROGRESSION;

    state.streak.introSeen = false;
    state.streak.resetTimestamp = getStreakBattleRoyaleResetTimestamp(now);
    delete state.streak.finishedTimestamp;
  }

  // Delete the join time reference and ready the event status.
  delete state.streak.joinedTimestamp;

  setStreakBattleRoyaleStatus(state, 'available');
}

/**
 * Join the streak event.
 * @param state - The mutable state object.
 * @param now - The current timestamp.
 * @returns A boolean indicating whether the player has joined the streak event or not.
 */
export function joinStreakBattleRoyale(
  state: MutableState,
  now: number,
): boolean {
  /**
   * skip if streak event feature is not enabled,
   * not initialised,
   * or not in an 'available' state.
   */
  if (
    !isStreakBattleRoyaleEnabled(state) ||
    !state.streak ||
    getStreakBattleRoyaleStatus(state) !== 'available'
  )
    return false;

  // Set the joined timestamp.
  state.streak.joinedTimestamp = now;

  // Reset progress
  state.streak.milestone = 0;

  // Store the current streak level at the time of joining.
  state.streak.streakBeforeJoining = state.match2.winStreak;

  // Reset the last user lifecycle.
  delete state.streak.lastUserLifecycle;

  // Reset the last milestone seen cache back to 0.
  setStreakBattleRoyaleLastMilestoneSeen(state, 0);

  // Set event status to active.
  setStreakBattleRoyaleStatus(state, 'active');

  return true;
}

/**
 * Progresses the streak event milestone
 * @param state - The mutable state object.
 * @param now - The current timestamp.
 * @returns A boolean indicating whether the streak event is completed or not.
 */
export function progressStreakBattleRoyale(
  state: MutableState,
  now: number,
): boolean {
  // Skip if streak event is not active
  if (!isStreakBattleRoyaleActive(state, now)) return false;

  // Get the current goal for the user
  const goal = getStreakBattleRoyaleGoal(state);

  // Progress streak event milestone
  state.streak!.milestone++;

  const completed = state.streak!.milestone >= goal;

  // Set event status to completed, if applicable.
  if (completed) {
    setStreakBattleRoyaleStatus(state, 'completed', now);
  }

  // Return whether the streak event is completed or not
  return completed;
}

export function incrementStreakBattleRoyaleCompleteCount(
  state: MutableState,
): void {
  // Skip if streak event is not enabled or not initialised.
  if (!isStreakBattleRoyaleEnabled(state) || !state.streak) return;
  // Progress streak event milestone
  state.streak.completeCount++;
}
